import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa6';
import { Helmet } from 'react-helmet';

const ProjectPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState({});
  const [images, setImages] = useState([]);
  const [projectsList, setProjectsList] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjectsList = async () => {
      const querySnapshot = await getDocs(collection(db, 'projects'));
      const projects = [];
      querySnapshot.forEach((doc) => {
        projects.push({ id: doc.id, ...doc.data() });
      });
      // Optionnel : tu peux trier par année ou autre critère ici
      setProjectsList(projects);
    };

    fetchProjectsList();
  }, []);

  const currentIndex = projectsList.findIndex((proj) => proj.id === id);
  const prevProject = currentIndex > 0 ? projectsList[currentIndex - 1] : null;
  const nextProject = currentIndex < projectsList.length - 1 ? projectsList[currentIndex + 1] : null;

  useEffect(() => {
    const fetchProjectData = async () => {
      const docRef = doc(db, 'projects', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const projectData = docSnap.data();
        setProject(projectData);
        setImages(projectData.images || []);
      } else {
        console.log("Aucun document trouvé !");
      }
    };

    const fetchProjectsList = async () => {

      const q = query(
        collection(db, 'projects'),
        where('isVisible', '==', true),
        orderBy('year', 'desc'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      const projects = [];
      querySnapshot.forEach((doc) => {
        projects.push({ id: doc.id, ...doc.data() });
      });

      setProjectsList(projects);
    };

    fetchProjectData();
    fetchProjectsList();
  }, [id]);

  return (

    <>
      <Helmet>
        <title>{`WEAREBIAM | ${project.projectName}`}</title>
        <meta
          name="description"
          content={project.projectType || 'Creative Project by WEAREBIAM'}
        />

        {/* Open Graph tags */}
        <meta property="og:title" content={`WEAREBIAM | ${project.projectName}`} />
        <meta
          property="og:description"
          content={project.projectType || 'Creative Project by WEAREBIAM'}
        />
        <meta property="og:image" content={project.coverUrl} />
        <meta property="og:url" content={`https://wearebiam.com/work/project/${id}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <motion.div
        className='flex flex-col max-w-[1440px] gap-10 w-full min-h-screen bg-white lg:p-[5rem] p-[1.25rem]'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >

        <div className="flex lg:flex-row flex-col w-full lg:gap-[50px] gap-10 lg:mt-[100px] mt-[100px]">

          {/* Gauche */}
          <motion.section
            className="lg:w-2/5 w-full"
            initial={{ x: -40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => navigate(-1)}
              className="text-black text-[20px] hover:opacity-70 transition"
              title="Retour"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <p className="text-lightgrey2 uppercase">{project.projectType}</p>
              <p className="text-black uppercase lg:text-[42px] text-[32px] font-bold ppmori">
                {project.projectName}
              </p>
              <p className="flex gap-5 text-lightgrey2">Year <span className='text-black'>{project.year}</span></p>

              <div
                className="lg:hidden block w-full mb-5 aspect-[5/4] bg-cover bg-center rounded-lg"
                style={{ backgroundImage: `url(${project.coverUrl})` }}
              />
            </div>

            <motion.p
              className="text-black ppmori lg:mt-[30px] lg:mb-[50px] mb-5"
              dangerouslySetInnerHTML={{ __html: project?.content }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            />

            <div className="flex flex-col lg:gap-[50px] gap-5">
              <div className="grid justify-between grid-cols-2 w-full">
                <p className='text-lightgrey2'>
                  Client <br />
                  <span className='text-black'>{project.client}</span>
                </p>

                <p className='text-lightgrey2'>
                  Mission <br />
                  <span className='text-black'>
                    {Array.isArray(project.missions) && project.missions.length > 0 ? (
                      project.missions.map((mission, index) => (
                        <div key={index}>{mission}</div>
                      ))
                    ) : (
                      <span className="text-gray-500">---</span>
                    )}
                  </span>
                </p>
              </div>

              {/* Liens */}
              <div className="grid justify-between grid-cols-2 w-full">
                {project.projectWebsite && (
                  <a className="text-lightgrey2 hover:text-black w-fit font-light lg:text-[20px] text-[24px]"
                    href={project.projectWebsite} target="_blank" rel="noopener noreferrer">
                    Website
                  </a>
                )}
                {project.projectInstagram && (
                  <a className="text-lightgrey2 hover:text-black w-fit font-light lg:text-[20px] text-[24px]"
                    href={project.projectInstagram} target="_blank" rel="noopener noreferrer">
                    Instagram
                  </a>
                )}
              </div>
            </div>
          </motion.section>

          {/* Droite */}
          <motion.section
            className="flex flex-col gap-10 lg:w-3/5 w-full"
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col gap-5">
              {/* Cover principale */}
              {project.coverUrl && (
                <motion.div
                  className="lg:block hidden w-full aspect-[5/4] bg-cover bg-center rounded-lg"
                  style={{ backgroundImage: `url(${project.coverUrl})` }}
                  initial={{ scale: 0.98, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />
              )}

              {/* Images en pattern */}
              {Array.isArray(project.images) &&
                project.images.reduce((acc, url, index) => {
                  if (index % 4 === 0) acc.push([]);
                  acc[acc.length - 1].push(url);
                  return acc;
                }, []).map((group, groupIndex) => (
                  <motion.div
                    key={groupIndex}
                    className="flex flex-col gap-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 + groupIndex * 0.2 }}
                  >
                    <div className="grid grid-cols-3 gap-2.5">
                      {group.slice(0, 3).map((url, index) => (
                        <div
                          key={index}
                          className="w-full h-[40dvh] bg-cover bg-center rounded-lg"
                          style={{ backgroundImage: `url(${url})` }}
                        />
                      ))}
                    </div>

                    {group[3] && (
                      <div
                        className="w-full h-[60dvh] bg-cover bg-center rounded-lg"
                        style={{ backgroundImage: `url(${group[3]})` }}
                      />
                    )}
                  </motion.div>
                ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between font-bold">
              {prevProject ? (
                <Link
                  to={`/work/project/${prevProject.id}`}
                  className="lg:text-[38px] ppmori uppercase text-lightgrey2 leading-none hover:text-black transition"
                >
                  Previous <br /> Project
                </Link>
              ) : <span />}

              {nextProject ? (
                <Link
                  to={`/work/project/${nextProject.id}`}
                  className="lg:text-[38px] ppmori uppercase text-right text-lightgrey2 leading-none hover:text-black transition"
                >
                  Next <br /> Project
                </Link>
              ) : <span />}
            </div>

          </motion.section>

        </div>
      </motion.div>

    </>

  );
};

export default ProjectPage;
